import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { fetchPaymentStatus } from "@/lib/api";
import { useEntitlement } from "@/context/EntitlementContext";
import { trackEvent } from "@/lib/analytics";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 20; // 40s total — gives Polar webhook time to fire

/**
 * PaymentSuccess — landed here from Stripe OR Polar checkout.
 *
 * Stripe redirect:  ?session_id=cs_test_...
 * Polar  redirect:  ?provider=polar&checkout_id=polar_c_...
 *
 * For Stripe we poll /api/payments/status/:session_id which resolves quickly.
 * For Polar there is no client-facing status endpoint — the webhook is the
 * source of truth. So we just poll entitlement.status until it flips to
 * "premium", then celebrate. Both flows share the same UI.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh: refreshEntitlement } = useEntitlement();
  const [status, setStatus] = useState("polling"); // polling | paid | timeout | error
  const [attempts, setAttempts] = useState(0);
  const started = useRef(false);

  const sessionId = params.get("session_id");
  const provider = params.get("provider") || (sessionId ? "stripe" : null);
  const checkoutId = params.get("checkout_id");

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // No usable identifier at all → truly an error case (someone visited the
    // page directly without a checkout).
    if (!provider) {
      setStatus("error");
      return;
    }

    let cancelled = false;

    const pollPolar = async (attempt = 0) => {
      if (cancelled) return;
      setAttempts(attempt + 1);
      try {
        const ent = await refreshEntitlement();
        if (ent?.is_premium) {
          setStatus("paid");
          trackEvent("upgrade_checkout_paid", { provider: "polar", checkout_id: checkoutId });
          return;
        }
      } catch { /* non-fatal */ }
      if (attempt + 1 >= MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      setTimeout(() => pollPolar(attempt + 1), POLL_INTERVAL_MS);
    };

    const pollStripe = async (attempt = 0) => {
      if (cancelled) return;
      setAttempts(attempt + 1);
      try {
        const data = await fetchPaymentStatus(sessionId);
        if (data.payment_status === "paid") {
          setStatus("paid");
          trackEvent("upgrade_checkout_paid", { provider: "stripe", session_id: sessionId });
          try { await refreshEntitlement(); } catch { /* non-fatal */ }
          return;
        }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch { /* 404 during propagation is fine, keep polling */ }
      if (attempt + 1 >= MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      setTimeout(() => pollStripe(attempt + 1), POLL_INTERVAL_MS);
    };

    if (provider === "polar") pollPolar(0);
    else pollStripe(0);

    return () => { cancelled = true; };
  }, [sessionId, provider, checkoutId, refreshEntitlement]);

  const providerLabel = provider === "polar" ? "Polar" : "Stripe";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="payment-success">
      {status === "polling" && (
        <>
          <Loader2 size={40} className="animate-spin text-press-red mb-4" />
          <div className="ink-stamp red mb-3">Confirming your subscription…</div>
          <div className="font-headline uppercase text-2xl sm:text-3xl font-black tracking-tighter">
            {providerLabel} is stamping the paperwork.
          </div>
          <p className="mt-3 font-body italic text-ink-muted max-w-md">
            Hang tight — this normally takes a couple of seconds. ({attempts}/{MAX_ATTEMPTS})
          </p>
        </>
      )}

      {status === "paid" && (
        <>
          <CheckCircle2 size={56} className="text-press-red mb-4" />
          <div className="ink-stamp red mb-3">Welcome to Premium</div>
          <div className="font-headline uppercase text-3xl sm:text-5xl font-black tracking-tighter">
            You&apos;re in the pit seats now.
          </div>
          <p className="mt-4 font-body italic text-ink-muted max-w-lg">
            Unlimited Cinema Mode is live on this account. Auto-advance and Era Binge are yours to play with.
          </p>
          <div className="mt-8 flex gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-press-red text-paper border-2 border-press-red px-5 py-3 font-mono uppercase text-xs tracking-widest hover:bg-ink hover:border-ink"
              data-testid="payment-success-home-btn"
            >
              Start watching →
            </Link>
            <button
              onClick={() => navigate("/favourites")}
              className="inline-flex items-center gap-2 border-2 border-ink px-5 py-3 font-mono uppercase text-xs tracking-widest hover:bg-ink hover:text-paper"
            >
              My favourites
            </button>
          </div>
        </>
      )}

      {status === "timeout" && (
        <>
          <AlertTriangle size={40} className="text-press-red mb-4" />
          <div className="font-headline uppercase text-2xl sm:text-3xl font-black tracking-tighter">
            Still processing…
          </div>
          <p className="mt-3 font-body italic text-ink-muted max-w-md">
            {providerLabel} is taking a bit longer than usual. Your subscription will activate as soon as they confirm.
            Refresh this page in a minute — or head home and try Cinema Mode.
          </p>
          <div className="mt-6 flex gap-3">
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 border-2 border-ink px-5 py-3 font-mono uppercase text-xs tracking-widest hover:bg-ink hover:text-paper"
            >
              Retry check
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-press-red text-paper border-2 border-press-red px-5 py-3 font-mono uppercase text-xs tracking-widest hover:bg-ink hover:border-ink"
            >
              Go home
            </Link>
          </div>
        </>
      )}

      {status === "error" && (
        <>
          <AlertTriangle size={40} className="text-press-red mb-4" />
          <div className="font-headline uppercase text-2xl sm:text-3xl font-black tracking-tighter">
            Something went wrong
          </div>
          <p className="mt-3 font-body italic text-ink-muted max-w-md">
            We couldn&apos;t verify this checkout. If you were charged, no worries — {providerLabel} will refund automatically or you can reach out.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 border-2 border-ink px-5 py-3 font-mono uppercase text-xs tracking-widest hover:bg-ink hover:text-paper"
          >
            Back home
          </Link>
        </>
      )}
    </div>
  );
}
